import { Permission } from '@/entities/permissions';
import type { SymbolKey } from '@/entities/symbol';
import ExportButton from '@/features/_shared/chart/ExportButton';
import { canExportWidget } from '@/features/dashboard';
import type { FormAppWidget } from '@/features/dashboard';

import { usePermission } from '@/features/_shared/user';
import { ExportSelectionSource } from '@/features/data-hub';
import {
  collectOtherGroupSymbols,
  hasOtherGroupSymbols
} from '@/features/markets/components/CommoditiesTable/useOtherGroupData';
import { getSymbolsFromRows } from '@/features/markets/components/lib/getSymbolsFromRows';
import { getAllMiniTableSymbols } from '@/features/markets/components/MiniTablesContainer/lib/getMiniTableSymbols';
import { getColumnSymbolKey } from '@/features/markets/components/SpotHistoryTable/spotHistoryColumn';
import type {
  BaseTabType,
  MarketsMinitableType,
  MarketsSpotHistoryTableType,
  MarketsTableType
} from '@/features/markets/types';
import { requestSpotSelection } from '@/features/symbol-selector';
import { useAppDispatch } from '@/store/hooks';
import { openExportDrawer } from '@/store/slices/drawer.slice';
import { setExport } from '@/store/slices/export.slice';

function getSymbolsFromTable(tab: MarketsTableType): SymbolKey[] {
  return tab.sections.flatMap(section => getSymbolsFromRows(section.rows));
}

function getSymbolsFromSpotHistoryTable(tab: MarketsSpotHistoryTableType): SymbolKey[] {
  if (tab.items?.length) {
    return tab.items.map(item => item.symbol.key);
  }

  return tab.columns.flatMap(column => {
    const symbolKey = getColumnSymbolKey(column);

    return symbolKey ? [symbolKey] : [];
  });
}

const isExportSupportedTab = (
  tab: BaseTabType
): tab is MarketsTableType | MarketsMinitableType | MarketsSpotHistoryTableType =>
  tab.type === 'minitable' ||
  ((tab.type === 'table' || (tab.type === 'spot_history_table' && Boolean(tab.columns.length))) &&
    canExportWidget(tab as FormAppWidget));

const ExportWidgetButton = ({ tab }: { tab: BaseTabType }) => {
  const { hasPermission } = usePermission();

  const isPermissionGranted = hasPermission(Permission.ExportView);

  const dispatch = useAppDispatch();

  const handleClick = () => {
    switch (tab.type) {
      case 'table': {
        if (hasOtherGroupSymbols(tab.sections)) {
          dispatch(setExport({ exportNameBase: tab.name }));
          dispatch(
            openExportDrawer({
              showHistory: false,
              source: ExportSelectionSource.WIDGET_BUTTON,
              restrictedTableExportContext: {
                tabName: tab.name,
                symbols: collectOtherGroupSymbols(tab.sections)
              }
            })
          );
        } else {
          dispatch(requestSpotSelection({ symbols: getSymbolsFromTable(tab) }));
          dispatch(setExport({ exportNameBase: tab.name }));
          dispatch(
            openExportDrawer({ showHistory: false, source: ExportSelectionSource.WIDGET_BUTTON })
          );
        }
        break;
      }
      case 'minitable': {
        dispatch(requestSpotSelection({ symbols: getAllMiniTableSymbols(tab.tables) }));
        dispatch(setExport({ exportNameBase: tab.name }));
        dispatch(
          openExportDrawer({ showHistory: false, source: ExportSelectionSource.WIDGET_BUTTON })
        );
        break;
      }
      case 'spot_history_table': {
        dispatch(requestSpotSelection({ symbols: getSymbolsFromSpotHistoryTable(tab) }));
        dispatch(setExport({ exportNameBase: tab.name }));
        dispatch(
          openExportDrawer({ showHistory: false, source: ExportSelectionSource.WIDGET_BUTTON })
        );
        break;
      }
      default:
    }
  };

  if (!isExportSupportedTab(tab)) {
    return null;
  }

  return (
    <ExportButton
      disabled={!isPermissionGranted}
      handleClick={handleClick}
      title='Export widget'
    />
  );
};

export default ExportWidgetButton;
